import type { Assessment } from "../assessment-types";

export const ATTACHMENT_ASSESSMENT: Assessment = {
  slug: "attachment",
  emoji: "🧷",
  title: "Attachment Patterns",
  tagline: "How closeness and distance feel to you",
  intro:
    "This reflects on how you tend to relate to closeness, distance and connection in relationships. There are no right or wrong answers, only patterns worth noticing.",
  questions: [
    { id: "q1", text: "Do you find yourself needing frequent reassurance that someone still cares about you?" },
    { id: "q2", text: "Do you feel uneasy or on edge when a partner or friend needs some space?" },
    { id: "q3", text: "Do you pull away or shut down when someone gets emotionally close to you?" },
    { id: "q4", text: "Do you assume people will eventually leave or let you down?" },
    { id: "q5", text: "Do you find it hard to ask directly for what you need in a relationship?" },
    { id: "q6", text: "Do you replay conversations, worrying you said or did the wrong thing?" },
    { id: "q7", text: "Do you keep people at arm's length even when you want closeness?" },
    { id: "q8", text: "Do you feel most comfortable when a relationship has clear, steady rhythms?" },
    { id: "q9", text: "Do small silences or delayed replies make you feel anxious about where you stand?" },
    { id: "q10", text: "Do you struggle to trust that someone's kindness towards you is genuine?" },
    { id: "q11", text: "Do you notice yourself testing people to see if they will stay?" },
    { id: "q12", text: "Do you feel torn between wanting closeness and wanting to protect yourself from it?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest fairly settled patterns around closeness and distance.",
      body:
        "Relationships seem to feel workable for you most of the time, without a strong pull towards anxiety or avoidance. Everyone has moments of doubt in connection, and that seems manageable here.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns related to attachment anxiety or avoidance.",
      body:
        "Closeness and distance can sometimes feel loaded, perhaps needing reassurance at times or wanting space at others. These responses often develop as sensible ways of managing uncertain relationships earlier in life.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a recurring pattern of anxious or avoidant attachment.",
      body:
        "It sounds like closeness can bring up real worry, or that distance feels like the safer option, quite often. This tends to be a learned strategy for staying safe in connection, not a flaw in you.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong and frequent pattern around attachment security.",
      body:
        "It seems like relationships regularly stir up either a fear of losing connection or a pull to protect yourself by keeping distance. These are adaptive responses that made sense given what you have lived through, and they can shift with support.",
    },
  ],
  next: [
    { label: "Understanding attachment", to: "/learn/attachment-styles" },
    { label: "Trauma responses explained", to: "/learn/trauma-responses" },
    { label: "Building healthier boundaries", to: "/learn/boundaries" },
    { label: "Try a grounding exercise", to: "/grounding" },
  ],
};
